/**
 * @file Defines the configuration registry for segment builder conditions.
 * This file centralizes the metadata needed to dynamically render the UI
 * for different types of segment conditions (events and user properties).
 * It maps backend identifiers (GraphQL Enums) to UI labels, sentence structures,
 * available operators, time frames, and filterable properties.
 */

import { CanonicalEventName, SupportedUserProperty, ConversionUnit } from '@/gql/graphql';

// --- Configuration Types --- //

/** UI representation of operators for event inclusion/exclusion */
export type InclusionOperator = 'has' | 'has_not';
/** UI representation of operators for property value comparisons */
export type ComparisonOperator = 'is' | 'is_not' | 'contains' | 'does_not_contain';

/** UI-friendly identifiers for time boundary types */
export const TIME_BOUNDARY_TYPES = {
  ANY_TIME: 'any_time',
  CONVERSION_WINDOW: 'conversionWindow',
  TIME_RANGE: 'timeRange'
} as const;
/** Type derived from TIME_BOUNDARY_TYPES constants */
export type TimeFrameType = (typeof TIME_BOUNDARY_TYPES)[keyof typeof TIME_BOUNDARY_TYPES];

/** Available units for Conversion Window (maps directly to GQL Enum) */
export type TimeUnit = ConversionUnit;

/**
 * Describes a property that can be used to filter an event or user attribute,
 * including its UI label and data type for input rendering.
 */
export interface FilterableProperty {
  id: SupportedUserProperty;
  label: string; // UI Label
  type: 'string' | 'number' | 'boolean' | 'date'; // Input type hint
  // TODO: Define available operators specific to this property type
  // TODO: Define available values (e.g., for dropdowns) later?
}

/**
 * Defines the metadata and UI configuration for a single segment condition type.
 * Used by ConditionRow to dynamically render UI based on the condition's ID.
 */
export interface ConditionDefinition {
  id: CanonicalEventName | SupportedUserProperty; // GQL key
  label: string; // Base label (e.g., "subscribed to newsletter")
  type: 'event' | 'user'; // Condition category
  /** Defines the visual sentence structure with placeholders for dynamic UI components */
  sentenceStructure: string[]; // e.g., ['Subscriber', '[Operator]', '[EventName]', '[TimeFrame]']
  /** Operators available for the primary comparison (e.g., 'has'/'has_not') */
  availableOperators: InclusionOperator[] | ComparisonOperator[];
  /** Time frame types applicable to this condition */
  availableTimeFrames: TimeFrameType[];
  /** Properties available for filtering this condition */
  availableProperties: FilterableProperty[];
}

// --- Condition Definitions --- //

const newsletterSubscribedConfig: ConditionDefinition = {
  id: CanonicalEventName.NewsletterSubscribed,
  label: 'Subscribed to Newsletter',
  type: 'event',
  sentenceStructure: ['Subscriber', '[Operator]', '[EventName]', '[TimeFrame]'],
  availableOperators: ['has', 'has_not'],
  availableTimeFrames: [
    TIME_BOUNDARY_TYPES.ANY_TIME,
    TIME_BOUNDARY_TYPES.CONVERSION_WINDOW,
    TIME_BOUNDARY_TYPES.TIME_RANGE
  ],
  availableProperties: [] // None currently defined
};

const productViewedConfig: ConditionDefinition = {
  id: CanonicalEventName.ProductViewed,
  label: 'Viewed Product',
  type: 'event',
  sentenceStructure: [
    'Subscriber',
    '[Operator]',
    '[EventName]',
    '[TimeFrame]' /* '[PropertyFilters]' */
  ],
  availableOperators: ['has', 'has_not'],
  availableTimeFrames: [
    TIME_BOUNDARY_TYPES.ANY_TIME,
    TIME_BOUNDARY_TYPES.CONVERSION_WINDOW,
    TIME_BOUNDARY_TYPES.TIME_RANGE
  ],
  availableProperties: [] // TODO: Define filterable properties
};

// Add basic configs for new shopping events
const checkoutCompletedConfig: ConditionDefinition = {
  id: CanonicalEventName.CheckoutCompleted,
  label: 'Made a Purchase',
  type: 'event',
  sentenceStructure: [
    'Subscriber',
    '[Operator]',
    '[EventName]',
    '[TimeFrame]' /* '[PropertyFilters]' */
  ],
  availableOperators: ['has', 'has_not'],
  availableTimeFrames: [
    TIME_BOUNDARY_TYPES.ANY_TIME,
    TIME_BOUNDARY_TYPES.CONVERSION_WINDOW,
    TIME_BOUNDARY_TYPES.TIME_RANGE
  ],
  availableProperties: [] // TODO: Define filterable properties (e.g., order value, items)
};

const collectionViewedConfig: ConditionDefinition = {
  id: CanonicalEventName.CollectionViewed,
  label: 'Viewed Collection',
  type: 'event',
  sentenceStructure: [
    'Subscriber',
    '[Operator]',
    '[EventName]',
    '[TimeFrame]' /* '[PropertyFilters]' */
  ],
  availableOperators: ['has', 'has_not'],
  availableTimeFrames: [
    TIME_BOUNDARY_TYPES.ANY_TIME,
    TIME_BOUNDARY_TYPES.CONVERSION_WINDOW,
    TIME_BOUNDARY_TYPES.TIME_RANGE
  ],
  availableProperties: [] // TODO: Define filterable properties (e.g., collection name)
};

const productAddedToCartConfig: ConditionDefinition = {
  id: CanonicalEventName.ProductAddedToCart,
  label: 'Added Product to Cart',
  type: 'event',
  sentenceStructure: [
    'Subscriber',
    '[Operator]',
    '[EventName]',
    '[TimeFrame]' /* '[PropertyFilters]' */
  ],
  availableOperators: ['has', 'has_not'],
  availableTimeFrames: [
    TIME_BOUNDARY_TYPES.ANY_TIME,
    TIME_BOUNDARY_TYPES.CONVERSION_WINDOW,
    TIME_BOUNDARY_TYPES.TIME_RANGE
  ],
  availableProperties: [] // TODO: Define filterable properties (e.g., product title)
};

const checkoutStartedConfig: ConditionDefinition = {
  id: CanonicalEventName.CheckoutStarted,
  label: 'Started Checkout',
  type: 'event',
  sentenceStructure: [
    'Subscriber',
    '[Operator]',
    '[EventName]',
    '[TimeFrame]' /* '[PropertyFilters]' */
  ],
  availableOperators: ['has', 'has_not'],
  availableTimeFrames: [
    TIME_BOUNDARY_TYPES.ANY_TIME,
    TIME_BOUNDARY_TYPES.CONVERSION_WINDOW,
    TIME_BOUNDARY_TYPES.TIME_RANGE
  ],
  availableProperties: [] // TODO: Define filterable properties
};

const pageViewedConfig: ConditionDefinition = {
  id: CanonicalEventName.PageViewed,
  label: 'Viewed Page',
  type: 'event',
  sentenceStructure: [
    'Subscriber',
    '[Operator]',
    '[EventName]',
    '[TimeFrame]' /* '[PropertyFilters]' */
  ],
  availableOperators: ['has', 'has_not'],
  availableTimeFrames: [
    TIME_BOUNDARY_TYPES.ANY_TIME,
    TIME_BOUNDARY_TYPES.CONVERSION_WINDOW,
    TIME_BOUNDARY_TYPES.TIME_RANGE
  ],
  availableProperties: [] // TODO: Define filterable properties (e.g., page title)
};

// Update registry map interface and object
interface ConditionRegistryMap {
  [CanonicalEventName.NewsletterSubscribed]?: ConditionDefinition;
  [CanonicalEventName.ProductViewed]?: ConditionDefinition;
  [CanonicalEventName.CheckoutCompleted]?: ConditionDefinition;
  [CanonicalEventName.CollectionViewed]?: ConditionDefinition;
  [CanonicalEventName.ProductAddedToCart]?: ConditionDefinition;
  [CanonicalEventName.CheckoutStarted]?: ConditionDefinition;
  [CanonicalEventName.PageViewed]?: ConditionDefinition;
}

/**
 * Central registry mapping GQL condition identifiers (CanonicalEventName, SupportedUserProperty)
 * to their corresponding UI configuration definitions (ConditionDefinition).
 */
export const conditionRegistry: ConditionRegistryMap = {
  [CanonicalEventName.NewsletterSubscribed]: newsletterSubscribedConfig,
  [CanonicalEventName.ProductViewed]: productViewedConfig,
  [CanonicalEventName.CheckoutCompleted]: checkoutCompletedConfig,
  [CanonicalEventName.CollectionViewed]: collectionViewedConfig,
  [CanonicalEventName.ProductAddedToCart]: productAddedToCartConfig,
  [CanonicalEventName.CheckoutStarted]: checkoutStartedConfig,
  [CanonicalEventName.PageViewed]: pageViewedConfig
};

/**
 * Helper function to safely retrieve a condition definition from the registry.
 * @param id The GQL identifier (CanonicalEventName or SupportedUserProperty).
 * @returns The corresponding ConditionDefinition or undefined if not found.
 */
export const getConditionDefinition = (
  id: CanonicalEventName | SupportedUserProperty | undefined
): ConditionDefinition | undefined => {
  if (!id) return undefined;
  // Use type assertion as TS might not perfectly infer the key correlation
  return conditionRegistry[id as keyof ConditionRegistryMap];
};
